import { Card, Stack, Table, TableBody, TableContainer } from '@mui/material';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  TopUpWithdrawTxType,
  TopupWithdrawFilters,
  TopupWithdrawFilterKey,
  TopupWithdrawFilterValue,
} from 'src/type/topup-withdraw';
import { isAfter } from 'src/utils/format-time';
import Scrollbar from 'src/components/scrollbar';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableNoData from 'src/components/table/table-no-data';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import StatusTabFilter, { StatusTabFilterOption } from 'src/components/ui-kit/status-tab-filter';
import { useBoolean } from 'src/hooks/use-boolean';
import { TransactionType, TransactionStatus, TransactionBadgeType } from 'src/type/transaction';
import usePagination from 'src/hooks/use-pagination';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import axios, { endpoints } from 'src/utils/axios';
import { Pagination } from 'src/type/utils';
import { toastError } from 'src/utils/format-error';
import TableLoading from 'src/components/table/table-loading';
import { emptyRows } from 'src/components/table/utils';
import { UserType } from 'src/type/user';
import TopupWithdrawTableRow from './topup-withdraw-table-row';
import TopupWithdrawTableFiltersResult from './topup-withdraw-table-filters-result';
import TopupWithdrawTableToolbar from './topup-withdraw-table-toolbar';
import TopupCreditCreateDialog from './topup-credit-create-dialog';
import WithdrawCreditCreateDialog from './withdraw-credit-create-dialog';

const defaultFilters: TopupWithdrawFilters = {
  name: null,
  type: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

const tableHead = [
  { id: '', width: 60 },
  { id: 'invoiceNumber', label: 'ลูกค้า' },
  { id: 'createDate', label: 'วันที่', width: 180 },
  { id: 'dueDate', label: 'วันที่อนุมัติ', width: 200 },
  { id: 'sent', label: 'จำนวน', width: 120 },
  { id: 'status', label: 'สถานะ', width: 120 },
  { id: 'action', width: 60 },
];

type Props = {
  user?: UserType;
  rootReload?: () => void;
};

export default function TopupWithdrawView({ user, rootReload }: Props) {
  const initialize = useBoolean(true);
  const [filters, setFilters] = useState(defaultFilters);

  const [badge, setBadge] = useState<TransactionBadgeType>({
    total: 0,
    waiting: 0,
    pending: 0,
    success: 0,
    rejected: 0,
    cancel: 0,
    failed: 0,
  });

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<TopUpWithdrawTxType>();

  const dateError = isAfter(filters.startDate?.toDate(), filters.endDate?.toDate());

  const topupForm = useBoolean(false);
  const withdrawForm = useBoolean(false);
  const reload = useBoolean(false);

  const canReset =
    !!filters.name ||
    filters.type !== 'all' ||
    filters.status !== 'all' ||
    (!!filters.startDate && !!filters.endDate);

  useEffect(() => {
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      ...fDateRange(filters.startDate, filters.endDate),

      type:
        filters.type === 'all'
          ? `${TransactionType.TOPUP_CREDIT},${TransactionType.WITHDRAW_CREDIT}`
          : filters.type,
      status: filters.status === 'all' ? undefined : filters.status,
      page: currentPage,
      limit: rowsPerPage,
      userId: user?.uid || filters.name?.value,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetTx(params);
  }, [
    currentPage,
    rowsPerPage,
    filters.startDate,
    filters.endDate,
    filters.name?.value,
    filters.status,
    filters.type,
    reload.value,
  ]);

  const handleGetTx = useCallback(async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const { data: _badge } = await axios.get<TransactionBadgeType>(
        endpoints.transactions.badges,
        {
          params: {
            type: `${TransactionType.TOPUP_CREDIT},${TransactionType.WITHDRAW_CREDIT}`,
          },
        }
      );
      setBadge(_badge);
      const { data } = await axios.get<Pagination<TopUpWithdrawTxType>>(
        endpoints.transactions.get,
        {
          params: fParams(params),
        }
      );

      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  }, []);

  const handleFilters = useCallback(
    (name: TopupWithdrawFilterKey, value: TopupWithdrawFilterValue) => {
      onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    onResetPage();
    setFilters(defaultFilters);
  }, []);

  const handleReload = useCallback(() => {
    // console.log('handleReload');

    onResetPage();
    if (rootReload) {
      rootReload();
    }
    reload.onToggle();
  }, []);

  const tabs: StatusTabFilterOption = useMemo(
    () => [
      { value: 'all', label: 'ทั้งหมด', color: 'default', count: badge.total },
      {
        value: TransactionStatus.SUCCESS,
        label: 'สำเร็จ',
        color: 'success',
        count: badge.success,
      },
      {
        value: TransactionStatus.PENDING,
        label: 'รอตรวจสอบ',
        color: 'warning',
        count: badge.pending,
      },
      {
        value: TransactionStatus.REJECTED,
        label: 'ปฏิเสธ',
        color: 'error',
        count: badge.rejected,
      },
      // {
      //   value: TransactionStatus.CANCEL,
      //   label: 'ยกเลิก',
      //   color: 'info',
      //   count: badge.cancel,
      // },
    ],
    [badge]
  );

  return (
    <>
      <Stack spacing={3}>
        <Card>
          {!user && (
            <StatusTabFilter
              value={filters.status}
              onChange={(newValue) => handleFilters('status', newValue)}
              options={tabs}
            />
          )}

          <TopupWithdrawTableToolbar
            filters={filters}
            onFilters={handleFilters}
            dateError={dateError}
            disabledSearch={!!user}
            enableCreate={!!user}
            onCreateTopup={topupForm.onTrue}
            onCreateWithdraw={withdrawForm.onTrue}
          />

          {canReset && (
            <TopupWithdrawTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={meta.totalResults}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              <Table sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={tableHead} />

                <TableBody>
                  <TableLoading loading={initialize.value} rows={rowsPerPage}>
                    {tableData.map((row) => (
                      <TopupWithdrawTableRow key={row.id} row={row} handleReload={handleReload} />
                    ))}

                    <TableEmptyRows
                      show={!initialize.value}
                      height={76}
                      emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                    />

                    <TableNoData notFound={!tableData.length} />
                  </TableLoading>
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={meta.totalResults}
            page={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Card>
      </Stack>
      {!!topupForm.value && !!user && (
        <TopupCreditCreateDialog
          open={topupForm.value}
          onClose={topupForm.onFalse}
          user={user}
          handleReload={handleReload}
        />
      )}
      {!!withdrawForm.value && !!user && (
        <WithdrawCreditCreateDialog
          open={withdrawForm.value}
          onClose={withdrawForm.onFalse}
          user={user}
          handleReload={handleReload}
        />
      )}
    </>
  );
}
